import { Registry, type Identified } from './Registry';

/**
 * Maden / malzeme tanımları.
 * Buradaki tek tanım hem dünya üretimini (derinlik, nadirlik, sertlik)
 * hem de üretilen ekipmanın istatistiklerini besler.
 * Yeni maden eklemek için sadece bu listeye bir kayıt eklemek yeterlidir;
 * ekipmanları, tarifleri ve loot ağırlıkları otomatik türetilir.
 */

export type Rarity = 'common' | 'uncommon' | 'rare' | 'veryRare' | 'legendary';

export interface GearStats {
  /** Kılıç temel hasarı. */
  damage: number;
  /** Araç kullanım hızı çarpanı (yüksek = hızlı). */
  speed: number;
  /** Dayanıklılık (kullanım sayısı). */
  durability: number;
  /** Bu seviyeden düşük sertlikteki kaynaklar kırılabilir. */
  harvestLevel: number;
  /** Zırh parçası başına savunma. */
  defense: { head: number; chest: number; legs: number; feet: number };
}

export interface MaterialDef extends Identified {
  name: string;
  icon: string;
  rarity: Rarity;
  /** Ekipman üretilebilir mi? (kömür/redstone gibi maddeler üretilemez) */
  gear: GearStats | null;
  /** Dünyada damar olarak oluşur mu? */
  vein: {
    /** Yüzeyden itibaren derinlik aralığı (birim). */
    minDepth: number;
    maxDepth: number;
    /** Chunk başına beklenen damar sayısı. */
    frequency: number;
    /** Damar başına blok/düğüm sayısı. */
    clusterMin: number;
    clusterMax: number;
    /** Kırmak için gereken kazma seviyesi. */
    hardness: number;
  } | null;
  /** BEDO'ya satış değeri (Kogox). */
  value: number;
  color: number;
}

export const materials = new Registry<MaterialDef>('materials');

const noDefense = { head: 0, chest: 0, legs: 0, feet: 0 };

materials.register(
  // --- Temel malzemeler ---
  {
    id: 'wood',
    name: 'Odun',
    icon: '🪵',
    rarity: 'common',
    value: 2,
    color: 0x8d6e46,
    vein: null,
    gear: { damage: 4, speed: 1, durability: 60, harvestLevel: 1, defense: { head: 1, chest: 2, legs: 2, feet: 1 } },
  },
  {
    id: 'stone',
    name: 'Taş',
    icon: '🪨',
    rarity: 'common',
    value: 3,
    color: 0x8b8b8b,
    vein: { minDepth: 0, maxDepth: 999, frequency: 6, clusterMin: 3, clusterMax: 8, hardness: 1 },
    gear: { damage: 6, speed: 1.3, durability: 130, harvestLevel: 2, defense: { head: 2, chest: 3, legs: 3, feet: 2 } },
  },
  // --- Madenler ---
  {
    id: 'coal',
    name: 'Kömür',
    icon: '🖤',
    rarity: 'common',
    value: 5,
    color: 0x2b2b2b,
    vein: { minDepth: 2, maxDepth: 90, frequency: 3.2, clusterMin: 4, clusterMax: 10, hardness: 1 },
    gear: null,
  },
  {
    id: 'copper',
    name: 'Bakır',
    icon: '🟠',
    rarity: 'common',
    value: 8,
    color: 0xc07040,
    vein: { minDepth: 4, maxDepth: 70, frequency: 2.4, clusterMin: 3, clusterMax: 8, hardness: 2 },
    gear: { damage: 7, speed: 1.5, durability: 190, harvestLevel: 2, defense: { head: 2, chest: 4, legs: 3, feet: 2 } },
  },
  {
    id: 'iron',
    name: 'Demir',
    icon: '⚙️',
    rarity: 'uncommon',
    value: 14,
    color: 0xd8d8d8,
    vein: { minDepth: 6, maxDepth: 110, frequency: 2, clusterMin: 3, clusterMax: 7, hardness: 2 },
    gear: { damage: 9, speed: 1.8, durability: 280, harvestLevel: 3, defense: { head: 3, chest: 6, legs: 5, feet: 3 } },
  },
  {
    id: 'silver',
    name: 'Gümüş',
    icon: '🥈',
    rarity: 'uncommon',
    value: 26,
    color: 0xe8e8f0,
    vein: { minDepth: 14, maxDepth: 120, frequency: 1.1, clusterMin: 2, clusterMax: 5, hardness: 3 },
    gear: { damage: 11, speed: 2.1, durability: 300, harvestLevel: 3, defense: { head: 3, chest: 6, legs: 5, feet: 4 } },
  },
  {
    id: 'gold',
    name: 'Altın',
    icon: '🥇',
    rarity: 'rare',
    value: 40,
    color: 0xf2c14a,
    vein: { minDepth: 18, maxDepth: 130, frequency: 0.8, clusterMin: 2, clusterMax: 5, hardness: 3 },
    gear: { damage: 8, speed: 2.6, durability: 160, harvestLevel: 3, defense: { head: 3, chest: 5, legs: 4, feet: 3 } },
  },
  {
    id: 'redstone',
    name: 'Redstone',
    icon: '🔴',
    rarity: 'uncommon',
    value: 18,
    color: 0xd11a1a,
    vein: { minDepth: 20, maxDepth: 140, frequency: 1.3, clusterMin: 4, clusterMax: 9, hardness: 3 },
    gear: { damage: 7, speed: 1.6, durability: 210, harvestLevel: 3, defense: { head: 2, chest: 5, legs: 4, feet: 2 } },
  },
  {
    id: 'emerald',
    name: 'Zümrüt',
    icon: '💚',
    rarity: 'rare',
    value: 90,
    color: 0x2ecc71,
    vein: { minDepth: 30, maxDepth: 150, frequency: 0.4, clusterMin: 1, clusterMax: 3, hardness: 4 },
    gear: { damage: 13, speed: 2.4, durability: 520, harvestLevel: 4, defense: { head: 4, chest: 7, legs: 6, feet: 4 } },
  },
  {
    id: 'diamond',
    name: 'Elmas',
    icon: '💎',
    rarity: 'veryRare',
    value: 140,
    color: 0x67e8f9,
    vein: { minDepth: 40, maxDepth: 170, frequency: 0.3, clusterMin: 1, clusterMax: 4, hardness: 4 },
    gear: { damage: 16, speed: 2.8, durability: 780, harvestLevel: 5, defense: { head: 5, chest: 9, legs: 7, feet: 5 } },
  },
  {
    id: 'obsidian',
    name: 'Obsidyen',
    icon: '⬛',
    rarity: 'rare',
    value: 70,
    color: 0x241b33,
    vein: { minDepth: 45, maxDepth: 200, frequency: 0.9, clusterMin: 2, clusterMax: 6, hardness: 5 },
    gear: { damage: 15, speed: 1.4, durability: 900, harvestLevel: 5, defense: { head: 5, chest: 10, legs: 8, feet: 5 } },
  },
  {
    id: 'blackDiamond',
    name: 'Siyah Elmas',
    icon: '🔷',
    rarity: 'legendary',
    value: 420,
    color: 0x1b1b2f,
    vein: { minDepth: 70, maxDepth: 240, frequency: 0.08, clusterMin: 1, clusterMax: 2, hardness: 6 },
    gear: { damage: 22, speed: 3.1, durability: 1400, harvestLevel: 6, defense: { head: 7, chest: 12, legs: 10, feet: 7 } },
  },
  {
    id: 'netherite',
    name: 'Netherite',
    icon: '🟫',
    rarity: 'legendary',
    value: 600,
    color: 0x4a3b3b,
    vein: { minDepth: 90, maxDepth: 280, frequency: 0.06, clusterMin: 1, clusterMax: 2, hardness: 7 },
    gear: { damage: 25, speed: 3, durability: 2000, harvestLevel: 7, defense: { head: 8, chest: 14, legs: 11, feet: 8 } },
  },
  // --- KG'ye özgü madenler ---
  {
    id: 'kogite',
    name: 'Kogit',
    icon: '🟡',
    rarity: 'rare',
    value: 120,
    color: 0xffd166,
    vein: { minDepth: 25, maxDepth: 160, frequency: 0.5, clusterMin: 2, clusterMax: 5, hardness: 4 },
    gear: { damage: 14, speed: 3.4, durability: 460, harvestLevel: 4, defense: { head: 4, chest: 7, legs: 6, feet: 5 } },
  },
  {
    id: 'voidsteel',
    name: 'Boşluk Çeliği',
    icon: '🔮',
    rarity: 'veryRare',
    value: 310,
    color: 0x7c4dff,
    vein: { minDepth: 60, maxDepth: 220, frequency: 0.12, clusterMin: 1, clusterMax: 3, hardness: 6 },
    gear: { damage: 20, speed: 2.9, durability: 1100, harvestLevel: 6, defense: { head: 6, chest: 11, legs: 9, feet: 6 } },
  },
  {
    id: 'venomcrystal',
    name: 'Zehir Kristali',
    icon: '🟣',
    rarity: 'veryRare',
    value: 260,
    color: 0xa855f7,
    // Yalnızca mor zehirli biyomda oluşur (biome filtresi WorldGen tarafında uygulanır).
    vein: { minDepth: 0, maxDepth: 120, frequency: 0.7, clusterMin: 2, clusterMax: 5, hardness: 5 },
    gear: { damage: 18, speed: 2.7, durability: 700, harvestLevel: 5, defense: { head: 5, chest: 9, legs: 8, feet: 6 } },
  },
);

/** Ekipman üretilebilen malzemeler (tarif ve item türetimi bunu kullanır). */
export const gearMaterials = (): MaterialDef[] => materials.filter((m) => m.gear !== null);

/** Dünyada damar olarak oluşan malzemeler. */
export const veinMaterials = (): MaterialDef[] => materials.filter((m) => m.vein !== null && m.id !== 'stone');

/** Nadirlik -> temel ağırlık (loot ve damar dağılımı için ortak ölçek). */
export const RARITY_WEIGHT: Readonly<Record<Rarity, number>> = Object.freeze({
  common: 100,
  uncommon: 45,
  rare: 16,
  veryRare: 5,
  legendary: 1,
});

export const RARITY_LABEL: Readonly<Record<Rarity, string>> = Object.freeze({
  common: 'Yaygın',
  uncommon: 'Az Nadir',
  rare: 'Nadir',
  veryRare: 'Çok Nadir',
  legendary: 'Efsanevi',
});

export const RARITY_COLOR: Readonly<Record<Rarity, string>> = Object.freeze({
  common: '#c8d2dc',
  uncommon: '#6ee7a8',
  rare: '#60a5fa',
  veryRare: '#c084fc',
  legendary: '#fbbf24',
});

export const noArmorDefense = noDefense;
